import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

/**
 * Submit-adjustment form extracted from SoAdjustmentModal. Includes the
 * delta+reason inputs and a live "after approve" preview badge.
 *
 * Props:
 *   so:         current SO doc
 *   deltaStr, reason         current input values
 *   onDeltaChange, onReasonChange
 *   submitting  boolean — disables the submit button
 *   onSubmit()  fn
 */
export default function SoAdjustmentSubmitForm({
  so, deltaStr, reason,
  onDeltaChange, onReasonChange,
  submitting, onSubmit,
}) {
  const preview = _computePreview(so, deltaStr);
  return (
    <div className="border border-border p-4 bg-secondary/20 space-y-3" data-testid="submit-adjustment-form">
      <div className="overline text-xs">Ajukan Penyesuaian</div>
      <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-3 items-end">
        <div>
          <Label className="text-xs uppercase tracking-widest">Delta (+/-)</Label>
          <Input
            type="number" step="any"
            placeholder="500 atau -50"
            value={deltaStr}
            onChange={(e) => onDeltaChange(e.target.value)}
            className="rounded-none h-11 font-mono"
            data-testid="input-adjustment-delta"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Alasan</Label>
          <Textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="Contoh: Customer minta tambah 500 SAK · Audit fisik selisih -50"
            rows={2}
            className="rounded-none"
            data-testid="input-adjustment-reason"
          />
        </div>
      </div>
      {preview && <PreviewBadge preview={preview} />}
      <Button
        onClick={onSubmit}
        disabled={submitting || !deltaStr || !reason.trim()}
        className="rounded-none font-bold uppercase tracking-wider"
        data-testid="submit-adjustment-btn"
      >
        {submitting ? "Mengirim..." : "Ajukan"}
      </Button>
    </div>
  );
}

function _computePreview(so, deltaStr) {
  const d = Number(deltaStr);
  if (!d || !so) return null;
  const total = Number(so.kuantiti_total || 0) + d;
  const sisa = Number(so.kuantiti_sisa || 0) + d;
  return {
    total, sisa,
    negativeSisa: sisa < 0,
    negativeTotal: total < 0,
  };
}

function PreviewBadge({ preview }) {
  const cls = preview.negativeTotal
    ? "border-destructive text-destructive bg-destructive/5"
    : preview.negativeSisa
      ? "border-amber-500 text-amber-800 bg-amber-50"
      : "border-emerald-500 text-emerald-800 bg-emerald-50";
  return (
    <div className={`text-xs p-2 border font-mono ${cls}`}>
      Setelah approve: total → <strong>{preview.total}</strong>,
      sisa → <strong>{preview.sisa}</strong>
      {preview.negativeTotal && " ⛔ Total < 0, akan ditolak saat approve."}
      {!preview.negativeTotal && preview.negativeSisa &&
        " ⚠ Sisa akan minus (over-quota) — masih diperbolehkan, tapi warning."}
    </div>
  );
}
